import AppCard from '@nxsys/components/AppCard';
import AppCardTabListContainer from '@nxsys/components/AppCardTabListContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import Payment from './components/Payment';
import Deduction from './components/Deduction';

type Props = {
  employeeId: string;
  defaultTab?: 'PAYMENT' | 'DEDUCTION';
  paymentModel?: string;
};

enum PAYMENT_DEDUCTION_TAB {
  PAYMENT = 'PAYMENT',
  DEDUCTION = 'DEDUCTION',
}

const PaymentDeduction = ({ employeeId, defaultTab, paymentModel }: Props) => {
  const { messages } = useTypedIntl();

  const tabs = useMemo(
    () => [
      {
        tab: messages['common.payment'],
        key: PAYMENT_DEDUCTION_TAB.PAYMENT,
      },
      {
        tab: messages['common.deduction'],
        key: PAYMENT_DEDUCTION_TAB.DEDUCTION,
      },
    ],
    [],
  );

  const [activeTab, setActiveTab] = useState<PAYMENT_DEDUCTION_TAB>(
    defaultTab ? PAYMENT_DEDUCTION_TAB[defaultTab] : PAYMENT_DEDUCTION_TAB.PAYMENT,
  );

  useEffect(() => {
    if (defaultTab) {
      setActiveTab(PAYMENT_DEDUCTION_TAB[defaultTab]);
    }
  }, [defaultTab]);

  const handleTabChange = (key: PAYMENT_DEDUCTION_TAB) => {
    setActiveTab(key);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case PAYMENT_DEDUCTION_TAB.PAYMENT:
        return <Payment employeeId={employeeId} paymentModel={paymentModel} />;
      case PAYMENT_DEDUCTION_TAB.DEDUCTION:
        return <Deduction employeeId={employeeId} paymentModel={paymentModel} />;
    }
  };

  const tabLabel =
    activeTab === PAYMENT_DEDUCTION_TAB.PAYMENT
      ? messages['common.payment']
      : messages['common.deduction'];

  // V2 flat layout when defaultTab is provided (sidebar navigation)
  if (defaultTab) {
    return (
      <div>
        <Typography.Title
          level={5}
          className="!mb-6 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
        >
          {tabLabel}
        </Typography.Title>
        {renderTabContent()}
      </div>
    );
  }

  // Legacy card layout
  return (
    <AppCard title={messages['common.paymentsAndDeductions']}>
      <AppCardTabListContainer
        tabList={tabs}
        activeTab={activeTab}
        handleTabChange={handleTabChange}
        className="!border-none"
      >
        <div className="p-[16px]">{renderTabContent()}</div>
      </AppCardTabListContainer>
    </AppCard>
  );
};

export default PaymentDeduction;
